import React from 'react';
import { Clock, ExternalLink, Video, PhoneCall, User } from 'lucide-react';
import { CallSession } from '@/features/admin/api/sessionsApi';
import { formatDate } from '../../../../../shared/utils/date';
import { DataTable, ColumnDef } from '../../../../../shared/components/molecules/Table/DataTable';

interface RecentCallsTableProps {
  sessions: CallSession[];
  isLoading: boolean;
  isOrgAdmin?: boolean;
  onSelectCall: (id: string) => void;
}

export function RecentCallsTable({
  sessions,
  isLoading,
  isOrgAdmin,
  onSelectCall
}: RecentCallsTableProps) {

  const columns: ColumnDef<CallSession>[] = [
    {
      header: 'Meeting / Call',
      key: 'meetingTitle',
      render: (call) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '0',
            backgroundColor: 'rgba(56, 189, 248, 0.08)',
            border: '1px solid var(--admin-card-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#38bdf8',
            flexShrink: 0
          }}>
            <Video size={14} />
          </div>
          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '13px', fontWeight: 700, color: 'var(--admin-card-text)' }}>
            {call.meetingTitle || 'Unnamed Sales Call'}
          </span>
        </div>
      )
    },
    {
      header: 'Host / Rep',
      key: 'owner',
      render: (call) => {
        const repName = (call as any).owner?.name || 'Sales Rep';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--admin-card-text)', fontWeight: 500 }}>
            <User size={12} style={{ color: 'var(--admin-card-subtext)' }} />
            {repName}
          </span>
        );
      }
    },
    {
      header: 'Platform',
      key: 'platform',
      render: (call) => (
        <span style={{ color: 'var(--admin-card-subtext)', textTransform: 'capitalize' }}>
          {call.platform || 'General Call'}
        </span>
      )
    },
    {
      header: 'Date & Time',
      key: 'startedAt',
      render: (call) => (
        <span style={{ color: 'var(--admin-card-subtext)' }}>
          {call.startedAt ? formatDate(call.startedAt, true) : 'N/A'}
        </span>
      )
    },
    {
      header: 'Duration',
      key: 'durationSeconds',
      render: (call) => {
        const durationText = call.durationSeconds
          ? `${Math.floor(call.durationSeconds / 60)}m ${call.durationSeconds % 60}s`
          : '0m';
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--admin-card-subtext)' }}>
            <Clock size={12} /> {durationText}
          </span>
        );
      }
    },
    {
      header: 'Buying Intent',
      key: 'finalBuyingIntentLabel',
      render: (call) => {
        const buyingIntent = call.finalBuyingIntentLabel || 'Neutral';
        const buyingIntentColor = buyingIntent.toLowerCase().includes('high') 
          ? '#10b981' 
          : buyingIntent.toLowerCase().includes('medium') 
          ? '#38bdf8' 
          : '#94a3b8';
        
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '0',
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: buyingIntentColor,
            backgroundColor: `${buyingIntentColor}15`,
            border: `1px solid ${buyingIntentColor}30`
          }}>
            {buyingIntent}
          </span>
        );
      }
    },
    {
      header: 'Battlecards',
      key: 'battlecardsUsed',
      render: (call) => (
        <span style={{ color: 'var(--admin-card-text)', fontWeight: 600 }}>
          {call.battlecardsUsed || 0} / {call.battlecardsGenerated || 0}
        </span>
      )
    },
    {
      header: 'Action',
      key: 'action',
      render: (call) => (
        <div style={{ textAlign: 'right' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelectCall(call.id);
            }}
            style={{
              padding: '6px 12px',
              borderRadius: '0',
              backgroundColor: '#ffffff',
              color: '#000000',
              border: 'none',
              fontSize: '11px',
              fontWeight: 700,
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            View Analysis <ExternalLink size={11} />
          </button>
        </div>
      )
    }
  ];

  return (
    <DataTable
      theme="admin"
      columns={columns}
      data={sessions}
      isLoading={isLoading}
      total={sessions.length} // DataTable doesn't render pagination anymore, but requires the prop
      page={1}
      limit={10}
      onPageChange={() => {}}
      onRowClick={(call) => onSelectCall(call.id)}
      emptyMessage="No Call Sessions Found"
    />
  );
}
